import { useState, useContext, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import FormContext from '../../context/form-context';
import { formValidation } from './formValidation';
import { STATUS } from '../../constants/form-status'
import { ACTION } from '../../constants/form-action'
import { MODE } from '../../constants/form-mode'
import { addNewSubmittedForm, getSubmittedFormByID, updateSubmittedForm } from "../../services/firebase/submittedForms/tb_submittedForms";
import dayjs from "dayjs";
import { stateActions } from './stateSetter'
import {  setAgentDetails, setAlert, setFormErrors, setFormInfo, setFormSignatories, setFormState, setFormTypeField, setPartiesFields, setSharedForms, setSupportingDocs, setTemplatesField } from '../../state/action';
import { getMainFieldsByFormType } from '../../services/firebase/mainFields/mainFieldsTable';
import generateUniqueId from 'generate-unique-id';
import useAuth from '../../hooks/useAuth';
import { fetchConfigurations } from '../../services/firebase/configuration/configurationTable';
import {  isEmptyObject } from '../../utils/utilities';
import { ROLES } from '../../constants/roles';
import { defaultForm, defaultType } from '../../constants/form-type';
import { uploadPartiesFiles } from './uploadUtils';

const useFormStepper = () => {
    const { auth } = useAuth();
    const { appState, dispatch } = useContext(FormContext)
    const [ activeStep, setActiveStep] = useState(0);
    const [ tempFormState, setTempFormState] = useState(appState.formState)
    const [signCanOverwrite , setSignCanOverwrite] = useState(true)
    const [ dataLoaded , setDataLoaded] = useState(false)
    const stepperClickable = appState.formInfo.status === STATUS.INCOMPLETE;
    const {userDetails} = appState
    const [agentRestricted , setAgentRestricted] = useState(false)
    const {id, role} = auth;
    const { transID} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state;  // Access the passed state
 
    useEffect(() => {
        
        const checkAgentRestriction = (form , type , status) => { 
            if(
                (form === defaultForm[1] && type === defaultType[0] ) //if BDC-SALES
            ){
                setAgentRestricted(true)
            }
        }

        const getSubmittedData = async () => {
            try {
                const data = await getSubmittedFormByID(transID, id, role);
                
                if(isEmptyObject(data)){
                    dispatch(setAlert({
                        show: true,
                        title: "Unauthorized Access",
                        body: `You don't have permission for this transaction`,
                        btnFunc: () => navigate('/')
                    }))
                    return
                }

                //set Agent Details
                const agentDetails = stateActions.updateAgentDetails(data);
                if (agentDetails) { dispatch(setAgentDetails(agentDetails)) }
                else { dispatch(setAgentDetails(appState.agentDetails)) }

                //set Form Type & set Fields
                const mainForm = await getMainFieldsByFormType(data.Form , data.Type);
                const formType = stateActions.updateFormTypeState(data, mainForm);
                dispatch(setFormTypeField(formType))

                // //set Selected Templates
                const selectedTemplates = stateActions.updateSelectedTemplatesState(data);
                dispatch(setTemplatesField(selectedTemplates))

                // //set Shared Forms
                const sharedForms = stateActions.updateSharedFormsState(data);
                dispatch(setSharedForms(sharedForms))

                const supportingDocs = stateActions.updateSupportingDocsState(data);
                dispatch(setSupportingDocs(supportingDocs))
                
                const formStateAndErrors = stateActions.updateFormAndErrorState(data ,formType);
                dispatch(setFormState(formStateAndErrors.formState))
                dispatch(setFormErrors(formStateAndErrors.formErrors))
                setTempFormState(formStateAndErrors.formState)

                //set count of Parties Fields 
                const propertyParties = stateActions.updatePartiesFieldsCount(formStateAndErrors.formState.mainValues, appState.partiesFieldsCount);
                dispatch(setPartiesFields(propertyParties))

                //set Signatories State
                const signatories = stateActions.updateSignatoriesState(data);
                dispatch(setFormSignatories(signatories))

                // form status , mode and transID
                dispatch(setFormInfo({
                    ...appState.formInfo , 
                    status: data.Status , 
                    transactionID: transID , 
                    mode: data.Status === STATUS.SUBMITTED ? MODE.CLOSED : MODE.EDIT,
                    action: (data.Status === STATUS.NEW || data.Status === STATUS.DRAFT) ? ACTION.UPDATE : ACTION.VIEW,
                    created: dayjs(new Date(data.Created?.seconds * 1000)).format('MMMM DD, YYYY hh:mm A')
                }))
                setDataLoaded(true)


                if( role === ROLES.AGENT ){
                    //special handling
                    //agent restriction
                    await checkAgentRestriction(formType.form, formType.type , data.Status)
                }

            }
            catch (error) {
                console.log(error)
                dispatch(setAlert({
                    show: true,
                    title: "Error",
                    body: `Transaction ID: ${transID} does not exist`,
                    btnFunc: () => navigate('/')
                }))
                
                
            }
        }
 
        try {
            const config = fetchConfigurations();
            config?.then( res => { 
                setSignCanOverwrite(res.SignatureOverwrite)
            })

        } catch (error) {
            console.log(error)
            dispatch(setAlert({
                show: true,
                title: "Error",
                body: `Failed fetching configuration`,
                btnFunc: () => navigate('/')
            }))
        }

        if (transID) {
            //check if existing in firebase.
            //timeout to avoid error upon submission.
            setTimeout(() => {
                getSubmittedData();
            }, 1000); 
    
            if(locationState){
                setActiveStep(locationState.step ? locationState.step : 1)
            }
        }
        else {
            setDataLoaded(true)
            dispatch(setFormInfo({
                ...appState.formInfo , 
                mode: MODE.NEW, 
                action: ACTION.CREATE 
            }))
            
        }
        
    }, [])

    const btnAction = {
        handleUpdate: () => {
            dispatch(setFormInfo({
                ...appState.formInfo , 
                action: ACTION.UPDATE 
            }))
            if(signCanOverwrite && appState.formInfo.status === STATUS.INCOMPLETE){
                dispatch(setAlert({show:true , title: 'Warning' , body: 'Please be aware that if you update the forms, it will require revising all the signatures.' , btnLabel: "Agree"}))
            }

        },
        handleCancel: () => {
            dispatch(setFormInfo({
                ...appState.formInfo , 
                action: ACTION.VIEW 
            }))
            
            //put back the state of mainValues
            dispatch(setFormState({...appState.formState, mainValues: tempFormState.mainValues}))

            // do this just to rerender the jsx component.
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
            setTimeout( () => { 
                setActiveStep((prevActiveStep) => prevActiveStep - 1);
            },1)

        },
        handleSaveAsDraft: () => {
            formSaveAsDraft();
        },
        handleSubmitNext: (action) => {
            const transactionID = getTransID();
            const existingID = transID;

            const validated = formValidation(activeStep, appState, dispatch, existingID, navigate, transactionID, action, signCanOverwrite, setTempFormState);
            // dispatch(setFormInfo({...appState.formInfo, action: ACTION.VIEW}))

            // setTempFormState(appState.formState) //set inside the formValidation function;
            if (validated) {
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
            }
        },
        handleNext: () => {
             setActiveStep((prevActiveStep) => prevActiveStep + 1);
        },
        handleBack: () => {
            setActiveStep((prevActiveStep) => prevActiveStep - 1);
        }, 
        handleReset: () => {
            setActiveStep(3);
        }
    }

    const formSaveAsDraft = async () => {
        const transactionID = getTransID();

        const partiesFilesLinks = await uploadPartiesFiles(appState.formState.mainValues,  transactionID, appState.userDetails , dispatch , setAlert)
        console.log(partiesFilesLinks)
        try {
            //Save to FireBase
            dispatch(setAlert({
                show: true,
                title: 'Processing Form Submission',
                body: `The form is currently being saved. <br/>Kindly wait a moment. `,
                showBtn:false
            }))
            const data = await addNewSubmittedForm({
                TransactionId: transactionID,
                Values: { ...appState.formState, mainValues: {...appState.formState.mainValues , ...partiesFilesLinks} },
                Status: appState.formInfo.status === STATUS.DRAFT || appState.formInfo.status === STATUS.NEW
                    ? STATUS.DRAFT : STATUS.STATUS.INCOMPLETE,
                Form: appState.formType.form,
                Type: appState.formType.type,
                FormSignatories: appState.formSignatories,
                CreatedBy: appState.userDetails,
                ModifiedBy: appState.userDetails, //logged in user
                Agent: appState.agentDetails
            });

            //set the form Information state
            dispatch(setFormInfo({
                ...appState.formInfo,
                action: appState.formInfo.status !== STATUS.INCOMPLETE ? ACTION.UPDATE : ACTION.VIEW,
                transactionID: transactionID,
                mode: MODE.EDIT,
                status: appState.formInfo.status !== STATUS.INCOMPLETE ? STATUS.DRAFT : STATUS.INCOMPLETE,
                created: appState.formInfo.status === STATUS.NEW ?
                    dayjs(new Date()).format('MMMM DD, YYYY hh:mm A') :
                    dayjs(new Date(data.Created?.seconds * 1000)).format('MMMM DD, YYYY hh:mm A'),
            }))

            //show modal confirmation
            dispatch(setAlert({show: true, title: "Success" , body: `The form has been saved. <br/><br/> Transaction ID: <b> ${transactionID} </b>` }))
            console.log('Save data as Draft...')

            // redirection
            if(!transID){ //if no existing id
                navigate(`/edit/${transactionID}`);
            }

            //remove the error or reset error state
            dispatch(setFormErrors({ ...appState.formErrors, mainValues: {} }))

        } catch (error) {
            console.log(error)
            dispatch(setAlert({
                show: true, 
                title: "Saving Error" , 
                body: `Server Error: ${error} <br/><br/> Please contact your administrator.` 
            }))
        }
        
    }

    const getTransID = () => {
        const formType = `${appState.formType.form.toUpperCase()}${appState.formType.type.toUpperCase()}`
        const dateString = dayjs(new Date()).format('YYYYMMDD');
        const randomDigits = generateUniqueId({length: 4, useLetters: false});

        const transactionID = transID ? transID : `${formType}_${dateString}_${randomDigits}`

        return transactionID;
    }

    return {
        activeStep,
        btnAction,
        appState,
        dataLoaded,
        setActiveStep,
        stepperClickable,
        signCanOverwrite,
        agentRestricted
    }
}

export default useFormStepper;